/**
 * POSInterface - Point of Sale style order entry
 * Fast, touch-friendly interface for taking orders
 * Includes delegate lookup and included meal option
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote,
  Smartphone, User, Receipt, X, Check, Grid3X3, Search,
  Percent, MessageSquare, UtensilsCrossed, UserSearch, Gift,
  QrCode, ChefHat
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSInterface = () => {
  const queryClient = useQueryClient();
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [orderType, setOrderType] = useState('dine_in');
  const [selectedTable, setSelectedTable] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount, setDiscount] = useState(0);
  const [orderNotes, setOrderNotes] = useState('');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [showDelegateSearch, setShowDelegateSearch] = useState(false);
  const [delegateSearch, setDelegateSearch] = useState('');
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const searchTimeout = useRef(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['dining-categories'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/categories`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch menu items
  const { data: menuItems = [] } = useQuery({
    queryKey: ['dining-menu-pos'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu?available_only=true`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch tables
  const { data: tables = [] } = useQuery({
    queryKey: ['dining-tables'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Search delegates (for event attendees)
  const { data: delegateResults = [], isFetching: searchingDelegates } = useQuery({
    queryKey: ['delegate-search', delegateSearch],
    queryFn: async () => {
      if (!delegateSearch || delegateSearch.length < 2) return [];
      const res = await fetch(`${API_URL}/api/admin/delegates/search?q=${encodeURIComponent(delegateSearch)}`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: delegateSearch.length >= 2
  });

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create order');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['dining-orders']);
      queryClient.invalidateQueries(['dining-tables']);
      queryClient.invalidateQueries(['dining-stats']);
      queryClient.invalidateQueries(['kitchen-orders']);
      setLastOrder(data);
      setShowPaymentDialog(false);
      setShowReceiptDialog(true);
      // Reset cart
      resetOrder();
      toast.success('Order created!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Reset order state
  const resetOrder = () => {
    setCart([]);
    setDiscount(0);
    setOrderNotes('');
    setCustomerName('');
    setCustomerPhone('');
    setSelectedTable('');
    setSelectedDelegate(null);
    setDelegateSearch('');
  };

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category_id === selectedCategory;
    const matchesSearch = !searchTerm || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Cart operations
  const addToCart = (item) => {
    const existing = cart.find(c => c.menu_item_id === item.id);
    if (existing) {
      setCart(cart.map(c => 
        c.menu_item_id === item.id 
          ? { ...c, quantity: c.quantity + 1 }
          : c
      ));
    } else {
      setCart([...cart, {
        menu_item_id: item.id,
        name: item.name,
        price: item.special_price || item.price,
        quantity: 1,
        notes: ''
      }]);
    }
    // Visual feedback
    toast.success(`Added ${item.name}`, { duration: 1000 });
  };

  const updateQuantity = (menuItemId, delta) => {
    setCart(cart.map(c => {
      if (c.menu_item_id === menuItemId) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : c;
      }
      return c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (menuItemId) => {
    setCart(cart.filter(c => c.menu_item_id !== menuItemId));
  };

  const updateItemNotes = (menuItemId, notes) => {
    setCart(cart.map(c => 
      c.menu_item_id === menuItemId ? { ...c, notes } : c
    ));
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountAmount = subtotal * (discount / 100);
  const total = subtotal - discountAmount;

  // Handle delegate selection
  const handleSelectDelegate = (delegate) => {
    setSelectedDelegate(delegate);
    setCustomerName(delegate.name);
    setShowDelegateSearch(false);
    setDelegateSearch('');
    toast.success(`Selected: ${delegate.name}`);
  };

  // Handle payment
  const handlePayment = (paymentMethod) => {
    const orderData = {
      order_type: orderType,
      table_id: orderType === 'dine_in' ? selectedTable : null,
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
      delegate_id: selectedDelegate?.id || null,
      items: cart,
      notes: orderNotes || null,
      discount_percent: discount,
      payment_method: paymentMethod
    };
    createOrderMutation.mutate(orderData);
  };

  const availableTables = tables.filter(t => t.status === 'available');

  // Debounced search using timeout
  const debouncedSetDelegateSearch = useCallback((value) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDelegateSearch(value);
    }, 300);
  }, []);

  return (
    <div className="h-screen flex bg-slate-100" data-testid="pos-interface">
      {/* Left Panel - Menu Items */}
      <div className="flex-1 flex flex-col p-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="h-8 w-8 text-orange-600" />
            <h1 className="text-xl font-bold">Point of Sale</h1>
          </div>
          <a 
            href="/kitchen" 
            target="_blank"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            <ChefHat className="h-4 w-4" />
            Open Kitchen Display
          </a>
        </div>

        {/* Search & Categories */}
        <div className="mb-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search menu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 text-lg"
              data-testid="menu-search"
            />
          </div>
          
          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className="whitespace-nowrap"
            >
              All Items
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat.id)}
                className="whitespace-nowrap"
              >
                {cat.icon} {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="flex-1 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <UtensilsCrossed className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">No items found</p>
              <p className="text-sm">Try a different search or category</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filteredItems.map((item) => (
                <Card
                  key={item.id}
                  className={`cursor-pointer hover:shadow-lg hover:scale-102 transition-all ${
                    !item.is_available ? 'opacity-50 cursor-not-allowed' : ''
                  } ${cart.find(c => c.menu_item_id === item.id) ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => item.is_available && addToCart(item)}
                  data-testid={`menu-item-${item.id}`}
                >
                  <CardContent className="p-3">
                    <div className="aspect-square bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg mb-2 flex items-center justify-center relative">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <UtensilsCrossed className="h-10 w-10 text-orange-400" />
                      )}
                      {item.is_special && (
                        <Badge className="absolute top-1 right-1 bg-yellow-500 text-xs">Special!</Badge>
                      )}
                      {/* Quantity badge if in cart */}
                      {cart.find(c => c.menu_item_id === item.id) && (
                        <div className="absolute bottom-1 right-1 bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm">
                          {cart.find(c => c.menu_item_id === item.id).quantity}
                        </div>
                      )}
                    </div>
                    <h3 className="font-medium text-sm line-clamp-2 leading-tight mb-1">{item.name}</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        {item.special_price ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs line-through text-gray-400">${item.price?.toFixed(2)}</span>
                            <span className="text-lg font-bold text-green-600">${item.special_price?.toFixed(2)}</span>
                          </div>
                        ) : (
                          <span className="text-lg font-bold">${item.price?.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                    {/* Dietary badges */}
                    <div className="flex gap-1 mt-1">
                      {item.is_vegetarian && <span className="text-xs" title="Vegetarian">🥬</span>}
                      {item.is_vegan && <span className="text-xs" title="Vegan">🌱</span>}
                      {item.is_gluten_free && <span className="text-xs bg-amber-100 px-1 rounded" title="Gluten Free">GF</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Cart */}
      <div className="w-full max-w-[420px] bg-white shadow-xl flex flex-col border-l">
        {/* Order Type Selection */}
        <div className="p-4 border-b bg-slate-50">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {[
              { key: 'dine_in', icon: '🍽️', label: 'Dine In' },
              { key: 'takeaway', icon: '📦', label: 'Takeaway' },
              { key: 'delivery', icon: '🚗', label: 'Delivery' }
            ].map(({ key, icon, label }) => (
              <Button
                key={key}
                variant={orderType === key ? 'default' : 'outline'}
                onClick={() => setOrderType(key)}
                className="h-12"
                data-testid={`order-type-${key}`}
              >
                <span className="mr-1">{icon}</span>
                {label}
              </Button>
            ))}
          </div>
          
          {orderType === 'dine_in' ? (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Table</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {availableTables.slice(0, 12).map((table) => (
                  <Button
                    key={table.id}
                    variant={selectedTable === table.id ? 'default' : 'outline'}
                    onClick={() => setSelectedTable(table.id)}
                    className="h-12 font-bold"
                    data-testid={`table-${table.table_number}`}
                  >
                    {table.table_number}
                  </Button>
                ))}
                {availableTables.length === 0 && (
                  <p className="col-span-4 text-center text-gray-500 py-2">No available tables</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Customer name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="h-12 flex-1"
                  data-testid="customer-name"
                />
                <Button
                  variant="outline"
                  className="h-12 px-3"
                  onClick={() => setShowDelegateSearch(true)}
                  title="Search delegates"
                >
                  <UserSearch className="h-5 w-5" />
                </Button>
              </div>
              <Input
                placeholder="Phone (optional)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="h-10"
                data-testid="customer-phone"
              />
              {selectedDelegate && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <User className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">{selectedDelegate.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedDelegate(null);
                      setCustomerName('');
                    }}
                    className="ml-auto h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShoppingCart className="h-16 w-16 mx-auto mb-4" />
              <p className="font-medium">Cart is empty</p>
              <p className="text-sm">Tap items from the menu to add</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item) => (
                <div 
                  key={item.menu_item_id}
                  className="bg-gray-50 rounded-lg p-3 border"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium flex-1 pr-2">{item.name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFromCart(item.menu_item_id)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.menu_item_id, -1)}
                        className="h-9 w-9 p-0"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-10 text-center font-bold text-lg">{item.quantity}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.menu_item_id, 1)}
                        className="h-9 w-9 p-0"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <span className="font-bold text-lg">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                  {/* Item notes */}
                  <Input
                    placeholder="Special instructions..."
                    value={item.notes}
                    onChange={(e) => updateItemNotes(item.menu_item_id, e.target.value)}
                    className="mt-2 h-8 text-sm bg-white"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totals & Checkout */}
        <div className="border-t p-4 space-y-3 bg-slate-50">
          {/* Discount */}
          <div className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-gray-400" />
            <Input
              type="number"
              placeholder="Discount %"
              value={discount || ''}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              className="h-10 bg-white"
              min="0"
              max="100"
              data-testid="discount-input"
            />
          </div>

          {/* Order notes */}
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-gray-400" />
            <Input
              placeholder="Order notes..."
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              className="h-10 bg-white"
              data-testid="order-notes"
            />
          </div>

          {/* Totals */}
          <div className="space-y-1 text-sm bg-white p-3 rounded-lg border">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal ({cart.length} items)</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount ({discount}%)</span>
                <span>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-2xl font-bold pt-2 border-t mt-2">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Pay Button */}
          <Button
            className="w-full h-16 text-xl font-bold"
            disabled={cart.length === 0 || (orderType === 'dine_in' && !selectedTable)}
            onClick={() => setShowPaymentDialog(true)}
            data-testid="pay-button"
          >
            <CreditCard className="h-6 w-6 mr-2" />
            PAY ${total.toFixed(2)}
          </Button>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Select Payment Method</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-4xl font-bold mb-6">${total.toFixed(2)}</p>
            <div className="grid grid-cols-2 gap-4">
              {/* Standard payment methods */}
              <Button
                className="h-24 text-lg flex-col"
                variant="outline"
                onClick={() => handlePayment('cash')}
                disabled={createOrderMutation.isPending}
                data-testid="pay-cash"
              >
                <Banknote className="h-8 w-8 mb-2" />
                Cash
              </Button>
              <Button
                className="h-24 text-lg flex-col"
                variant="outline"
                onClick={() => handlePayment('card')}
                disabled={createOrderMutation.isPending}
                data-testid="pay-card"
              >
                <CreditCard className="h-8 w-8 mb-2" />
                Card
              </Button>
              <Button
                className="h-24 text-lg flex-col"
                variant="outline"
                onClick={() => handlePayment('mobile')}
                disabled={createOrderMutation.isPending}
                data-testid="pay-mobile"
              >
                <Smartphone className="h-8 w-8 mb-2" />
                Mobile Pay
              </Button>
              <Button
                className="h-24 text-lg flex-col"
                variant="outline"
                onClick={() => handlePayment('account')}
                disabled={createOrderMutation.isPending}
                data-testid="pay-account"
              >
                <User className="h-8 w-8 mb-2" />
                Account
              </Button>
              
              {/* Included meal option - for delegates with meal access */}
              <Button
                className="h-24 text-lg flex-col col-span-2 bg-blue-50 border-blue-300 hover:bg-blue-100"
                variant="outline"
                onClick={() => handlePayment('included')}
                disabled={createOrderMutation.isPending}
                data-testid="pay-included"
              >
                <Gift className="h-8 w-8 mb-2 text-blue-600" />
                <span className="text-blue-700">Included Meal (No Charge)</span>
                <span className="text-xs text-blue-500 mt-1">For delegates with meal access</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delegate Search Dialog */}
      <Dialog open={showDelegateSearch} onOpenChange={setShowDelegateSearch}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserSearch className="h-5 w-5" />
              Search Delegate
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Input
              placeholder="Search by name or email..."
              onChange={(e) => debouncedSetDelegateSearch(e.target.value)}
              className="h-12"
              autoFocus
              data-testid="delegate-search-input"
            />
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {searchingDelegates && (
                <p className="text-center text-gray-500 py-4">Searching...</p>
              )}
              {!searchingDelegates && delegateSearch.length >= 2 && delegateResults.length === 0 && (
                <p className="text-center text-gray-500 py-4">No delegates found</p>
              )}
              {delegateResults.map((delegate) => (
                <div
                  key={delegate.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer border"
                  onClick={() => handleSelectDelegate(delegate)}
                  data-testid={`delegate-result-${delegate.id}`}
                >
                  <div>
                    <p className="font-medium">{delegate.name}</p>
                    <p className="text-sm text-gray-500">{delegate.email}</p>
                    {delegate.company && (
                      <p className="text-xs text-gray-400">{delegate.company}</p>
                    )}
                  </div>
                  <div className="text-right">
                    {delegate.meal_access && (
                      <Badge className="bg-green-100 text-green-700">Meal Access</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-6 w-6 text-green-500" />
              Order Complete
            </DialogTitle>
          </DialogHeader>
          {lastOrder && (
            <div className="py-4">
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-green-600">{lastOrder.order_number}</p>
                <p className="text-gray-500 capitalize">{lastOrder.order_type?.replace('_', ' ')}</p>
                {lastOrder.payment_method === 'included' && (
                  <Badge className="mt-2 bg-blue-100 text-blue-700">Included Meal</Badge>
                )}
              </div>
              
              <div className="border-t border-b py-4 space-y-2 max-h-48 overflow-y-auto">
                {lastOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.name}</span>
                    <span>${item.total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <div className="pt-4 space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>${lastOrder.subtotal?.toFixed(2)}</span>
                </div>
                {lastOrder.discount_amount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-${lastOrder.discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-2xl font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>${lastOrder.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 pt-2">
                  <span>Payment</span>
                  <span className="capitalize">{lastOrder.payment_method}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button className="w-full h-12" onClick={() => setShowReceiptDialog(false)}>
              <Receipt className="h-5 w-5 mr-2" />
              Start New Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSInterface;
